import React from "react";
import { Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { FileOutlined, History, UserOutlined, View, Tag as TagNotLinkedIcon } from "assets/icons";
import { IDocumentInfo } from "components/documentInfo/DocumentInfo";
import ViewDocument from "components/documentInfo/ViewDocument";
import dayjs from "dayjs";
import theme from "styles/theme";
import { IColumn } from "typings/global";
import { convertTimeSpanToTime, isRoleAuthorizedToReleaseVehicle, truncateText, unauthorizedRolesInYardManagementTableActions } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { getSaleChannelConfig } from "utils/saleChannel";
import SaleChannelStatus from "../components/saleChannelStatus";
import * as S from "./styled/index.styled";

const isInboundAction = 1;
const isAwaiting = 0;
const isAuthorized = 1;

const isPresented = 1;
const isNotReleased = 2;
const isInRadius = 8;
const isManobraProcess = 2;

const isEnableReleaseVehicle = [isAwaiting, isAuthorized];
const isEnabledVehicleStepStatus = [isPresented, isNotReleased, isInRadius];

const ColumnsTable = ({
  userData,
  handlesClick,
  allowRequestVehicleInboundAction,
  allowRequestDockEntryAction,
  allowRequestVehicleEntryAuthorizationAction,
  hasScheduling,
  isEnabledProcessId,
  hasRfidReadingActive,
}) => {
  const CARRIER = useTranslation({ id: "carrier" });
  const PLATE = useTranslation({ id: "plate" });
  const TRAILERS_PLATE = useTranslation({ id: "trailers_plate" });
  const PROCESSES = useTranslation({ id: "processes" });
  const CHANNEL = useTranslation({ id: "channel" });
  const DOCUMENTS = useTranslation({ id: "documents" });
  const STATUS = useTranslation({ id: "status" });
  const STAY = useTranslation({ id: "stay" });
  const ACTIONS = useTranslation({ id: "actions" });
  const RELEASE_VEHICLE = useTranslation({ id: "release_vehicle" });
  const CALL_DRIVER = useTranslation({ id: "call_driver" });
  const VIEW_ENTRY = useTranslation({ id: "view_entry" });
  const EVENTS_HISTORY = useTranslation({ id: "events_history" });
  const CHECKIN_DATE = useTranslation({ id: "checkin_date" });
  const SCHEDULING = useTranslation({ id: "scheduling" });
  const ALL = useTranslation({ id: "all" });
  const PRESENTED = useTranslation({ id: "presented_vehicle" });
  const NOT_RELEASED = useTranslation({ id: "not_released" });
  const RELEASED = useTranslation({ id: "released" });
  const IN_OPERATION = useTranslation({ id: "in_operation" });
  const INTERN_QUEUE = useTranslation({ id: "intern_queue" });
  const FINISH_OPERATION = useTranslation({ id: "finished_operation" });
  const IN_RADIUS = useTranslation({ id: "in_radius" });
  const VEHICLE_DOES_NOT_HAVE_A_TAG = useTranslation({ id: "attention_the_vehicle_does_not_have_a_registered_tag_give_a_new_one_to_the_driver" });
  const INVALID_STATUS = "--";
  const enabledProcess = [...isEnabledProcessId];

  const enableReleaseVehicle = (record) => {
    const isManobraOrEnabledProcess =
      record.processId === isManobraProcess || (isEnabledVehicleStepStatus.includes(record.vehicleStepStatus) && enabledProcess.includes(record.processId));
    return isManobraOrEnabledProcess && !isEnableReleaseVehicle.includes(record.vehicleEntryAuthorizationStatus);
  };

  const convertToVehicleStepDescription = (vehicleStepStatus) => {
    switch (vehicleStepStatus) {
      case 0:
        return ALL;
      case 1:
        return PRESENTED;
      case 4:
        return INTERN_QUEUE;
      case 2:
        return NOT_RELEASED;
      case 3:
        return RELEASED;
      case 5:
        return INTERN_QUEUE;
      case 6:
        return IN_OPERATION;
      case 7:
        return FINISH_OPERATION;
      case 8:
        return IN_RADIUS;
      default:
        return INVALID_STATUS;
    }
  };

  const scheduleColumn = {
    title: SCHEDULING,
    dataIndex: "schedule",
    key: "schedule",
    defaultSortOrder: null,
    sorter: (a, b, defaultSortOrder) => {
      const dateA = a.serviceStartTime ? dayjs(a.serviceStartTime) : null;
      const dateB = b.serviceStartTime ? dayjs(b.serviceStartTime) : null;
      if (!dateA && !dateB) return 0;
      if (!dateA) return defaultSortOrder !== "descend" ? 1 : -1;
      if (!dateB) return defaultSortOrder !== "descend" ? -1 : 1;
      return dateA.isBefore(dateB) ? -1 : 1;
    },
    width: "auto",
    render: (_, record) => (
      <span>
        <Tag color={theme.colors.grayTag} style={{ fontSize: "14px", color: theme.colors.text, fontWeight: "400" }}>
          {record.serviceStartTime !== null && record.serviceFinishTime
            ? `${dayjs.utc(record.serviceStartTime).format("DD/MM HH:mm")} - ${dayjs.utc(record.serviceFinishTime).format("HH:mm")}`
            : "--"}
        </Tag>
      </span>
    ),
  };

  const collums: Array<IColumn> = [
    {
      key: "carrier",
      width: "18%",
      title: CARRIER,
      dataIndex: "carrier",
      render: (carrier: string) => truncateText(carrier),
    },
    {
      key: "vehiclePlate",
      width: "9%",
      title: PLATE,
      dataIndex: "vehiclePlate",
      render: (_, record) => (
        <S.VehiclePlateContainer hasActiveTag={record.hasActiveTag}>
          <span>{record.vehiclePlate}</span>
          {hasRfidReadingActive && !record.hasActiveTag && typeof record.hasActiveTag === "boolean" && (
            <Tooltip placement="top" title={VEHICLE_DOES_NOT_HAVE_A_TAG} data-testid="tag-not-linked-tooltip">
              <TagNotLinkedIcon data-testid="tag-not-linked-icon" />
            </Tooltip>
          )}
        </S.VehiclePlateContainer>
      ),
    },
    {
      key: "trailersplate",
      width: "9%",
      title: TRAILERS_PLATE,
      dataIndex: "trailersplate",
      render: (trailersplate: ColumnsType<string>) => (trailersplate === undefined || trailersplate?.length === 0 ? "--" : <span>{trailersplate.join("\n")}</span>),
    },
    {
      key: "process",
      width: "auto",
      title: PROCESSES,
      dataIndex: "process",
      render: (process: ColumnsType<string>) => `${process === null ? "--" : process}`,
    },
    {
      key: "saleChannelDescription",
      dataIndex: "saleChannelDescription",
      title: CHANNEL,
      width: "7%",
      render: (_, record) => (getSaleChannelConfig(record?.saleChannelId) ? <SaleChannelStatus saleChannelId={record.saleChannelId} /> : "--"),
    },
    {
      title: DOCUMENTS,
      dataIndex: "documents",
      key: "documents",
      width: "6%",
      render: (documents: Array<IDocumentInfo>, record) => <ViewDocument record={record} documents={documents} handlesClick={handlesClick} />,
    },
    {
      title: STATUS,
      dataIndex: "vehicleStepStatus",
      key: "vehicleStepStatus",
      width: "11%",
      render: (vehicleStepStatus: number) => (
        <span>
          <Tag key={vehicleStepStatus} color={theme.colors.grayTag} style={{ fontSize: "16px", color: theme.colors.text, fontWeight: "400" }}>
            {convertToVehicleStepDescription(vehicleStepStatus)}
          </Tag>
        </span>
      ),
    },
    {
      title: CHECKIN_DATE,
      dataIndex: "eventTime",
      key: "eventTime",
      width: "10%",
      render: (_, record) => (record.eventTime !== null ? `${dayjs.utc(record.eventTime).local().format("DD/MM/YYYY HH:mm")}` : "--"),
    },
    {
      title: STAY,
      width: "9%",
      dataIndex: "lenghtOfStay",
      key: "lenghtOfStay",
      render: (_, record) => (record.lenghtOfStay !== null ? `${convertTimeSpanToTime(record.lenghtOfStay)}` : "--"),
    },
    {
      title: ACTIONS,
      width: "12%",
      dataIndex: "action",
      sorter: false,
      filterMultiple: false,
      key: "actions",
      render: (_, record) => (
        <S.ActionContainer
          hasVehicleInbound={allowRequestVehicleInboundAction}
          hasReleaseVehicle={allowRequestVehicleEntryAuthorizationAction}
          hasRequestDock={allowRequestDockEntryAction}
        >
          {allowRequestVehicleEntryAuthorizationAction && isRoleAuthorizedToReleaseVehicle(userData.role) && (
            <button data-testid="release-vehicle-action-register" onClick={() => enableReleaseVehicle(record) && handlesClick.handleClickReleaseVehicleAction(record)}>
              <Tooltip placement="top" title={RELEASE_VEHICLE}>
                <FileOutlined
                  data-testid="release-vehicle-action-icon"
                  className={enableReleaseVehicle(record) ? "releaseVehicleActionRegister" : "releaseVehicleActionRegisterDisabled"}
                />
              </Tooltip>
            </button>
          )}
          {(allowRequestVehicleInboundAction || allowRequestDockEntryAction) &&
            (record.local === isInboundAction && allowRequestVehicleInboundAction && !allowRequestDockEntryAction ? (
              <button hidden={unauthorizedRolesInYardManagementTableActions(userData.role)}>
                <Tooltip title={CALL_DRIVER} placement="top">
                  <UserOutlined className="vehicleActionRegisterDisabled" data-testid="vehicle-action-icon" />
                </Tooltip>
              </button>
            ) : (
              <button
                hidden={unauthorizedRolesInYardManagementTableActions(userData.role)}
                onClick={() => handlesClick.handleClickVehicleAction(record)}
                data-testid="vehicle-action-register"
              >
                <Tooltip placement="top" title={CALL_DRIVER}>
                  <UserOutlined className="vehicleActionRegister" data-testid="vehicle-action-icon" />
                </Tooltip>
              </button>
            ))}
          <button data-testid="view-register" onClick={() => handlesClick.handleClickView(record)}>
            <Tooltip placement="top" title={VIEW_ENTRY}>
              <View className="viewRegister" data-testid="view-register-icon" />
            </Tooltip>
          </button>
          <button onClick={() => handlesClick.handleClickVehicleEntryAuthorization(record)} data-testid="vehicle-entry-authorization">
            <Tooltip placement="top" title={EVENTS_HISTORY}>
              <History className={`vehicleEntryAuthorization${record.hasWarning ? "WithWarning" : ""}`} data-testid="vehicle-entry-authorization-icon" />
            </Tooltip>
          </button>
        </S.ActionContainer>
      ),
    },
  ].filter(Boolean);

  if (hasScheduling) {
    collums.unshift(scheduleColumn);
  }
  return collums;
};

export default ColumnsTable;
